import React from 'react';
import { Button, CircularProgress, Typography, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ResponsiveModal from './ResponsiveModal';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  /** Extra content (e.g. a reason TextField) rendered under the message. */
  children?: React.ReactNode;
  /** Show a warning icon — appropriate for destructive actions. Default true for error color. */
  destructive?: boolean;
}

/**
 * Reusable confirmation dialog.
 *
 * Now mobile-first (full-screen-friendly via ResponsiveModal), theme-aware, with
 * a clear visual cue for destructive actions. API is unchanged from the previous
 * version, so existing call sites keep working.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  loading = false,
  confirmText = 'تایید',
  cancelText = 'انصراف',
  confirmColor = 'error',
  children,
  destructive,
}) => {
  const isDestructive = destructive ?? confirmColor === 'error';

  return (
    <ResponsiveModal
      open={open}
      onClose={onCancel}
      title={title}
      maxWidth="xs"
      disableClose={loading}
      icon={isDestructive ? <WarningAmberIcon color="error" /> : undefined}
      id="confirm-dialog"
      actions={
        <>
          <Button onClick={onCancel} disabled={loading} color="inherit">
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            color={confirmColor}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      {typeof message === 'string' ? (
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {message}
        </Typography>
      ) : (
        message
      )}
      {children && <Box sx={{ mt: 2 }}>{children}</Box>}
    </ResponsiveModal>
  );
};

export default ConfirmDialog;
